"use client";

import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import type { AutoScrollType } from "embla-carousel-auto-scroll";
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("reInit", onSelect);
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation, api } = useCarousel();

  const pauseAutoplay = useCallback(() => {
    const autoScroll = api?.plugins()?.autoScroll as AutoScrollType | undefined;
    if (!autoScroll) return;
    if (autoScroll.isPlaying()) {
      autoScroll.stop();
    }
  }, [api]);

  const resumeAutoplay = useCallback(() => {
    const autoScroll = api?.plugins()?.autoScroll as AutoScrollType | undefined;
    if (!autoScroll) return;
    if (!autoScroll.isPlaying()) {
      autoScroll.play();
    }
  }, [api]);

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev, api } = useCarousel();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full max-sm:size-4",
        orientation === "horizontal"
          ? "-left-8 top-1/2 -translate-y-1/2 max-sm:-left-6"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={() => {
        const autoScroll = api?.plugins()?.autoScroll as AutoScrollType | undefined;
        if (autoScroll) {
          const resetOrStop =
            autoScroll.options.stopOnInteraction === false
              ? autoScroll.reset
              : autoScroll.stop;
          resetOrStop();
        }
        scrollPrev();
      }}
      {...props}
    >
      <ArrowLeft />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext, api } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full max-sm:size-4",
        orientation === "horizontal"
          ? "-right-8 top-1/2 -translate-y-1/2 max-sm:-right-6"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollNext}
      onClick={() => {
        const autoScroll = api?.plugins()?.autoScroll as AutoScrollType | undefined;
        if (autoScroll) {
          const resetOrStop =
            autoScroll.options.stopOnInteraction === false
              ? autoScroll.reset
              : autoScroll.stop;
          resetOrStop();
        }
        scrollNext();
      }}
      {...props}
    >
      <ArrowRight />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}
function CarouselAutoPlayButton({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { api } = useCarousel();
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!api) return;
    const autoScroll = api.plugins()?.autoScroll as AutoScrollType | undefined;
    if (!autoScroll) return;

    setIsPlaying(autoScroll.isPlaying());
    
    const onPlay = () => setIsPlaying(true);
    const onStop = () => setIsPlaying(false);
    const onReInit = () => setIsPlaying(autoScroll.isPlaying());

    api
      .on("autoScroll:play", onPlay)
      .on("autoScroll:stop", onStop)
      .on("reInit", onReInit);
      
    return () => {
      api
        .off("autoScroll:play", onPlay)
        .off("autoScroll:stop", onStop)
        .off("reInit", onReInit);
    };
  }, [api]);

  const toggleAutoplay = useCallback(() => {
    if (!api) return;
    const autoScroll = api.plugins()?.autoScroll as AutoScrollType | undefined;
    if (!autoScroll) return;
    if (autoScroll.isPlaying()) {
      autoScroll.stop();
    } else {
      autoScroll.play();
    }
  }, [api]);

  return (
    <Button
      className={cn("absolute size-8 rounded-full", className)}
      onClick={toggleAutoplay}
      type="button"
      variant={variant}
      size={size}
      {...props}
    >
      {isPlaying ? "Stop" : "Start"}
      <span className="sr-only">Autoplay</span>
    </Button>
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselAutoPlayButton,
  useCarousel,
};
